import { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  faChartColumn,
  faCircleNodes,
  faFileCertificate,
  faGear,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import { faChevronLeft } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion, transform } from "framer-motion";
import { ThemeProvider } from "styled-components";
import { DateTime } from "luxon";
import {
  faListUl,
  faKey,
  faIdCardClip,
  faSliders,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCheck,
  faExclamation,
  faBracketsCurly,
} from "@fortawesome/pro-solid-svg-icons";

import theme from "theme";
import useAuthenticatedCall from "core/hooks/useAuthenticatedCall";
import {
  getOrganizationDeployment,
  getOrganizationOnboarding,
  getCustomerSegments,
  getProductCatalogVersion,
} from "modules/admin/actions";
import { DEFAULT_ORG, PERMISSIONS } from "core/constants";
import Typography from "core/components/Typography";
import { Column } from "core/styles";
import Flex from "core/components/Flex";
import { SideMenuContainer, SubNav } from "modules/admin/SubNav";
import { ROLES } from "core/constants";
import {
  getEnv,
  ENVIRONMENTS,
  hasAnalyticsHubUserAccess,
} from "common/utilities";
import ReactJsonView from "@microlink/react-json-view";
import Modal from "core/components/Modal";
import { SecondaryButton } from "core/components/QueryBuilder/styles";
import AdjustableColumn from "core/components/AdjustableColumn";
import OrganizationHeading from "./OrganizationHeading";
import Field from "core/components/Field";
import { required, validate } from "common/validation";
import useBundleFilters from "core/hooks/useBundleFilters";
import ProductBundleMenu from "modules/admin/OrgProductLicenseForm/ProductBundleMenu";
import {
  ONBOARDING_LABELS,
  ONBOARDING_ICONS,
  ONBOARDING_VARIANTS,
  DEPLOYMENT_ICONS,
  DEPLOYMENT_STATUS_MAP,
  DEPLOYMENT_STATUS_PILL_VARIANTS,
  REFETCH_SHORT,
  REFETCH_LONG,
  dateTimeFormat,
  durationFormat,
} from "modules/admin/OrgProductLicenseForm/constants";
import {
  ScrollableMenu,
  StyledCheckboxItem,
  StyledSelectAll,
  SelectAllIcon,
  ViewJsonButton,
  JSONContainer,
} from "modules/admin/OrgProductLicenseForm/styles";
import { useOrganizationByIdQuery } from "core/queries";
import BundleFiltersProvider from "core/context/BundleFiltersProvider";
import useActiveMembership from "core/hooks/useActiveMembership";
import useNavigate from "core/hooks/useNavigate";
import buildStatusObject from "./OrgProductLicenseForm/buildStatusObject";

const StatusTooltip = ({ data, onCodeClick }) => {
  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          textAlign: "left",
          width: "100%",
        }}
      >
        <div style={{ position: "relative", whiteSpace: "break-spaces" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h4" color="white">
              Summary
            </Typography>
            {onCodeClick && (
              <ViewJsonButton onClick={onCodeClick}>
                <FontAwesomeIcon
                  icon={faBracketsCurly}
                  style={{
                    color: "#fff",
                    border: "1px solid #fff",
                    borderRadius: "100%",
                    padding: "4px",
                  }}
                />
              </ViewJsonButton>
            )}
          </div>
          <Typography variant="p" color="white">
            <strong>Status:</strong> {data.status}
          </Typography>
          {data.message && (
            <Typography variant="p" color="white">
              <strong>Message:</strong> {data.message}
            </Typography>
          )}
          <Typography variant="p" color="white">
            <strong>Start Time:</strong>{" "}
            {data.licenseDeploymentStart?.toFormat(dateTimeFormat)}
          </Typography>
          {data.licenseDeploymentFinish && (
            <Typography variant="p" color="white">
              <strong>Duration:</strong>{" "}
              {data.licenseDeploymentFinish
                .diff(data.licenseDeploymentStart)
                .toFormat(durationFormat)}
            </Typography>
          )}
        </div>
        {["succeeded"].includes(data.rawStatus) && (
          <>
            <div>
              <Typography variant="h4" color="white">
                BI
              </Typography>
              <Typography variant="p" color="white">
                <strong>Success:</strong> {data.isBiDeployed}
              </Typography>
              <Typography variant="p" color="white">
                <strong>Start Time:</strong>{" "}
                {data.biDeploymentStart.toFormat(dateTimeFormat)}
              </Typography>
              <Typography variant="p" color="white">
                <strong>Duration:</strong>{" "}
                {data.biDeploymentFinish
                  .diff(data.biDeploymentStart)
                  .toFormat(durationFormat)}
              </Typography>
            </div>
            <div>
              <Typography variant="h4" color="white">
                Data
              </Typography>
              <Typography variant="p" color="white">
                <strong>Success:</strong> {data.isDataDeployed}
              </Typography>
              <Typography variant="p" color="white">
                <strong>Start Time:</strong>{" "}
                {data.dataDeploymentStart.toFormat(dateTimeFormat)}
              </Typography>
              <Typography variant="p" color="white">
                <strong>Duration:</strong>{" "}
                {data.dataDeploymentFinish
                  .diff(data.dataDeploymentStart)
                  .toFormat(durationFormat)}
              </Typography>
            </div>
          </>
        )}
      </div>
    </ThemeProvider>
  );
};

const DetailsNavigationMenu = ({
  onboarding,
  isOnboardingFetching,
  deployment,
  isDeploymentFetching,
  orgIsActive,
  customerSegment,
}) => {
  const { activeMembership } = useActiveMembership();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    bundleOptionValues,
    areAllBundlesSelected,
    toggleAllBundles,
    updateBundleOptions,
  } = useBundleFilters();

  const locationIsProductPath = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((p) => !!p)
        .reverse()[2] === "product",
    [location]
  );

  const locationIsConfigure = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((p) => !!p)
        .reverse()[0] === "configure",
    [location]
  );

  const locationIsReview = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((p) => !!p)
        .reverse()[0] === "confirm",
    [location]
  );

  const productBasePath = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const prodIdx = parts.indexOf("product");
    if (prodIdx === -1) return location.pathname;
    return "/" + parts.slice(0, prodIdx + 1).join("/");
  }, [location]);

  const previousPage = locationIsConfigure
    ? productBasePath
    : `${productBasePath}/configure`;

  return (
    <SideMenuContainer>
      {!locationIsConfigure && !locationIsProductPath && !locationIsReview ? (
        <AnimatePresence mode="wait">
          <SubNav
            key="/"
            to="general"
            icon={faListUl}
            label="General"
            statusIcon={
              orgIsActive
                ? ONBOARDING_ICONS[onboarding?.rawStatus] || faExclamation
                : ONBOARDING_ICONS["inactive"]
            }
            statusVariant={
              orgIsActive
                ? ONBOARDING_VARIANTS[onboarding?.rawStatus] || "default"
                : "default"
            }
            isStatusLoading={isOnboardingFetching}
          />

          {[ROLES.systemAdmin, ROLES.systemViewer, ROLES.orgAdmin].includes(
            activeMembership.role
          ) && (
            <SubNav key="members" to="members" icon={faUsers} label="Members" />
          )}

          {[ROLES.systemAdmin, ROLES.systemViewer].includes(
            activeMembership.role
          ) && (
            <SubNav
              key="member-licenses"
              to="member-licenses"
              icon={faIdCardClip}
              label="Member Licenses"
            />
          )}

          {activeMembership.role === ROLES.systemAdmin && (
            <SubNav
              key="product"
              to="product/summary"
              icon={faKey}
              label="Product Licenses"
              statusIcon={
                DEPLOYMENT_ICONS[DEPLOYMENT_STATUS_MAP[deployment?.rawStatus]]
              }
              statusVariant={
                DEPLOYMENT_STATUS_PILL_VARIANTS[
                  DEPLOYMENT_STATUS_MAP[deployment?.rawStatus]
                ]
              }
              isStatusLoading={isDeploymentFetching}
              isDisabled={
                orgIsActive
                  ? ONBOARDING_VARIANTS[onboarding?.rawStatus] !== "success"
                  : true
              }
            />
          )}

          {/* System Viewers reach another org's hub from here, but the workbook
              APIs still gate them on their own access level, so check it too. */}
          {[ROLES.systemAdmin, ROLES.systemViewer].includes(
            activeMembership.role
          ) &&
            hasAnalyticsHubUserAccess(activeMembership) && (
              <SubNav
                key="analytics-hub"
                to="analytics-hub"
                icon={faChartColumn}
                label="Analytics Hub"
              />
            )}

          {[ROLES.systemAdmin, ROLES.systemViewer].includes(
            activeMembership.role
          ) && (
            <SubNav
              key="configurations"
              to="configurations/aliases"
              icon={faGear}
              label="Data Context"
            />
          )}

          {[ROLES.orgAdmin, ROLES.systemViewer, ROLES.systemAdmin].includes(
            activeMembership.role
          ) && (
            <SubNav
              key="credentials"
              to="credentials"
              icon={faFileCertificate}
              label="Data Credentials"
              isDisabled={
                orgIsActive
                  ? ONBOARDING_VARIANTS[onboarding?.rawStatus] !== "success"
                  : true
              }
            />
          )}

          {activeMembership.role === ROLES.systemAdmin && (
            <SubNav
              key="integrations"
              to="integrations"
              icon={faCircleNodes}
              label="Integrations"
              isDisabled={
                orgIsActive
                  ? ONBOARDING_VARIANTS[onboarding?.rawStatus] !== "success"
                  : true
              }
            />
          )}

          {[ROLES.systemAdmin, ROLES.systemViewer].includes(
            activeMembership.role
          ) && (
            <SubNav
              key="preferences"
              to="preferences"
              icon={faSliders}
              label="Preferences"
              isDisabled={
                orgIsActive
                  ? ONBOARDING_VARIANTS[onboarding?.rawStatus] !== "success"
                  : true
              }
            />
          )}
        </AnimatePresence>
      ) : (
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            marginBottom: "20px",
          }}
        >
          <SecondaryButton
            onClick={() => {
              navigate(previousPage, {
                state: { from: location.pathname },
              });
            }}
            style={{
              width: "fit-content",
              display: "flex",
              backgroundColor: "transparent",
              padding: "4px 24px 24px 0px",
              border: "none",
              fontSize: 18,
            }}
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              style={{
                marginRight: "5px",
                marginTop: "1px",
              }}
            />
            <span style={{ color: "#2A0F4D", fontSize: 18, fontWeight: 300 }}>
              Back
            </span>
          </SecondaryButton>

          <ScrollableMenu>
            <div
              style={{
                opacity: !locationIsConfigure ? 0.5 : 1,
                pointerEvents: !locationIsConfigure ? "none" : "unset",
              }}
            >
              <Typography
                variant="sub-text"
                style={{ fontWeight: 600, color: "#000000", marginBottom: 6 }}
                noMargin
              >
                Filter Products By
              </Typography>
              <StyledCheckboxItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <StyledSelectAll
                  type="checkbox"
                  variant="large"
                  isDisabled="false"
                  checked={areAllBundlesSelected}
                  onChange={toggleAllBundles}
                />
                <SelectAllIcon
                  className={areAllBundlesSelected ? "checked" : "unchecked"}
                  style={{
                    backgroundColor: areAllBundlesSelected ? "#541299" : "#fff",
                    border: areAllBundlesSelected
                      ? "none"
                      : "1px solid #dddbda",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{
                      display: areAllBundlesSelected ? "unset" : "none",
                    }}
                  />
                </SelectAllIcon>
                <span>Show All</span>
              </StyledCheckboxItem>

              <Field
                id="product-bundle-options"
                type="checkbox"
                validator={validate(required())}
                value={bundleOptionValues}
                grouped={true}
                onChange={updateBundleOptions}
                style={{
                  iconBackgroundColor: "#541299",
                }}
                hideError
                showTooltip={true}
                tooltipText={`The ${customerSegment} customer segment does not support this product bundle`}
              />
            </div>
            <ProductBundleMenu />
          </ScrollableMenu>
        </div>
      )}
    </SideMenuContainer>
  );
};

const OrganizationLayout = () => {
  const location = useLocation();
  const params = useParams();
  const { activeMembership } = useActiveMembership();

  const [onboardingRefetchInterval, setOnboardingRefetchInterval] =
    useState(REFETCH_LONG);
  const [deploymentRefetchInterval, setDeploymentRefetchInterval] =
    useState(REFETCH_LONG);

  const { orgId: organizationId } = params;

  const [isModalOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [jsonRootName, setJsonRootName] = useState(false);

  const openModalWithData = (data, rootName) => {
    setJsonRootName(rootName);
    setModalContent(data);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setJsonRootName(false);
    setModalContent({});
    setModalIsOpen(false);
  };

  const {
    isLoading: isFetching,
    isError: isOrgError,
    error: orgError,
    data: organization,
    isPlaceholderData: isPlaceholderOrg,
  } = useOrganizationByIdQuery({
    organizationId,
    placeholderData: DEFAULT_ORG,
    include: "licenses",
    select: (org) => {
      // Merge org with default
      return {
        ...DEFAULT_ORG,
        ...org,
        primaryContact: {
          ...DEFAULT_ORG.primaryContact,
          ...org?.primaryContact,
        },
        domain: org?.domain || [],
        licenses: {
          ...DEFAULT_ORG.licenses,
          ...org?.licenses,
          tableau_user_licenses: {
            ...DEFAULT_ORG.licenses.tableau_user_licenses,
            ...org?.licenses?.tableau_user_licenses,
          },
        },
        preferences: {
          bi_portal: {
            default_states:
              org?.preferences?.bi_portal?.default_states[0] ||
              DEFAULT_ORG.preferences.bi_portal.default_states,
          },
        },
      };
    },
  });

  // fetch onboarding data
  const fetchOnboardingCall = useAuthenticatedCall(getOrganizationOnboarding);
  const { isLoading: isOnboardingFetching, data: onboarding } = useQuery({
    queryKey: ["organization-onboarding", { organizationId }],
    queryFn: fetchOnboardingCall,
    staleTime: onboardingRefetchInterval,
    refetchInterval: onboardingRefetchInterval,
    select: (data) => {
      const formattedData = buildStatusObject(data, ONBOARDING_LABELS);

      return {
        ...formattedData,
        tooltip: (
          <StatusTooltip
            data={formattedData}
            onCodeClick={
              activeMembership.role === ROLES.systemAdmin
                ? () =>
                    openModalWithData(
                      {
                        onboarding: organization.onboarding,
                      },
                      organizationId
                    )
                : null
            }
          />
        ),
      };
    },
  });

  // fetch deployment data
  const fetchDeploymentCall = useAuthenticatedCall(getOrganizationDeployment);
  const {
    isLoading: isDeploymentFetching,
    data: deployment,
    refetch: refetchDeploymentStatus,
    isRefetching: isRefetchingDeploymentStatus,
  } = useQuery({
    queryKey: ["organization-deployment", { organizationId }],
    queryFn: fetchDeploymentCall,
    staleTime: deploymentRefetchInterval,
    refetchInterval: deploymentRefetchInterval,
    enabled: onboarding && onboarding.rawStatus === "succeeded",
    select: (data) => {
      const formattedData = buildStatusObject(data, DEPLOYMENT_STATUS_MAP);

      return {
        ...formattedData,
        tooltip: (
          <StatusTooltip
            data={formattedData}
            onCodeClick={() =>
              openModalWithData(
                {
                  licenses: {
                    license_validation:
                      organization.licenses.license_validation,
                    deployment: organization.licenses.deployment,
                  },
                },

                organizationId
              )
            }
          />
        ),
      };
    },
  });

  const fetchCustomerSegments = useAuthenticatedCall(getCustomerSegments);
  const { data: selectedCustomerSegment } = useQuery({
    queryKey: ["customer-segments"],
    queryFn: fetchCustomerSegments,
    enabled: !!organization?.customer_segment,
    select: (data) => {
      const customerSegment = Object.values(data).find(
        (item) => item.id === organization?.customer_segment
      );
      const customerSegmentName = customerSegment?.name;

      const isMedicareLicensable = Object.values(data).find(
        (item) => item.id === organization?.customer_segment
      )?.is_medicare_licensable;

      const env = getEnv();
      const isDevEnv = [ENVIRONMENTS.LOCAL, ENVIRONMENTS.DEV].includes(env);
      const isPrOrg = isDevEnv && customerSegment?.pr_org === organization?.id;
      const licensableProductIds = customerSegment.licensable_products;

      return {
        customerSegmentName,
        licensableProductIds,
        isMedicareLicensable,
        isPrOrg,
      };
    },
  });

  // update onboarding refetchInterval based on status
  useEffect(() => {
    if (
      onboardingRefetchInterval === REFETCH_SHORT &&
      [
        "not onboarded",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(onboarding?.rawStatus)
    ) {
      setOnboardingRefetchInterval(REFETCH_LONG);
    }

    if (
      onboardingRefetchInterval === REFETCH_LONG &&
      ![
        "not onboarded",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(onboarding?.rawStatus)
    ) {
      setOnboardingRefetchInterval(REFETCH_SHORT);
    }
  }, [onboarding?.rawStatus, onboardingRefetchInterval]);

  // update deployment refetchInterval based on status
  useEffect(() => {
    if (
      deploymentRefetchInterval === REFETCH_SHORT &&
      [
        "not deployed",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(deployment?.rawStatus)
    ) {
      setDeploymentRefetchInterval(REFETCH_LONG);
    }

    if (
      deploymentRefetchInterval === REFETCH_LONG &&
      ![
        "not deployed",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(deployment?.rawStatus)
    ) {
      setDeploymentRefetchInterval(REFETCH_SHORT);
    }
  }, [deployment?.rawStatus, deploymentRefetchInterval]);

  return (
    <BundleFiltersProvider customerSegment={selectedCustomerSegment}>
      <div style={{ position: "relative", height: "100%", overflow: "hidden" }}>
        <Flex direction="column" height="100%">
          <Flex
            direction="row"
            minWidth="1000px"
            height="100%"
            style={{ userSelect: "none" }}
          >
            <AdjustableColumn
              minWidth={265}
              maxWidth={305}
              leftPadding={40}
              minLeftPadding={20}
              rightPadding={
                location.pathname.includes("product/") &&
                !location.pathname.includes("product/summary")
                  ? 2
                  : 40
              }
              minRightPadding={
                location.pathname.includes("product/") &&
                !location.pathname.includes("product/summary")
                  ? 0
                  : 20
              }
              contain="none"
              style={{
                backgroundColor: "var(--sidebar-background-secondary)",
                borderRight: "1px solid var(--sidebar-secondary-border)",
                paddingTop: "46px",
                height: "100%",
                gap: "10px",
              }}
            >
              <Column alignItems="start">
                <OrganizationHeading
                  organization={organization}
                  isLoading={isFetching || isPlaceholderOrg || isOrgError}
                />

                <DetailsNavigationMenu
                  onboarding={onboarding}
                  isOnboardingFetching={isOnboardingFetching}
                  deployment={deployment}
                  isDeploymentFetching={isDeploymentFetching}
                  orgIsActive={organization?.active}
                  customerSegment={selectedCustomerSegment?.customerSegmentName}
                />
              </Column>
            </AdjustableColumn>

            <div
              style={{
                flexGrow: 1,
                position: "relative",
                height: "100%",
                overflowX: "auto",
              }}
            >
              <Flex
                direction="column"
                style={{
                  height: "100%",
                  overflow: "auto",
                  position: "relative",
                  paddingLeft: "64px",
                  paddingRight: "64px",
                  paddingTop: "40px",
                  paddingBottom: "24px",
                  userSelect: "text",
                }}
              >
                <Outlet />
              </Flex>
              <div
                id="stickyLicenseReview"
                style={{
                  width: "100%",
                  position: "absolute",
                  left: "0px",
                  bottom: "0px",
                  zIndex: 1000,
                  backgroundColor: "#ebe1f9",
                }}
              />
            </div>
          </Flex>
        </Flex>
        <Modal
          visible={isModalOpen}
          dismiss={closeModal}
          title="JSON Viewer"
          style={{
            width: "60vw",
            marginTop: "5vh",
          }}
        >
          <JSONContainer>
            <ReactJsonView
              name={jsonRootName}
              src={modalContent}
              collapsed={4}
              displayObjectSize={false}
              displayDataTypes={false}
              quotesOnKeys={false}
              displayArrayKey={false}
            />
          </JSONContainer>
        </Modal>
      </div>
    </BundleFiltersProvider>
  );
};

export default OrganizationLayout;
